import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Phone, Mail, ChevronRight, CheckCircle, Clock, FileText, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const faqs = [
  {
    question: "Do I need calibration after a windscreen replacement?",
    answer: "Yes, in almost all cases. If your vehicle has a forward-facing camera mounted on or near the windscreen (used for lane keeping, automatic emergency braking, adaptive cruise control, or other ADAS features), it must be recalibrated after the windscreen is replaced. Even a slight misalignment of the camera can cause these safety systems to malfunction or provide inaccurate readings. Most vehicle manufacturers require recalibration as part of their repair procedures, and many insurers now mandate it as a condition of warranty and liability coverage."
  },
  {
    question: "How long does windscreen camera calibration take?",
    answer: "A typical static calibration takes approximately 45 minutes to one hour, depending on the vehicle make and model. Dynamic calibration, which requires a test drive, may take a little longer. We also perform pre- and post-calibration diagnostic scans, which add a small amount of time but ensure the job is done correctly and fully documented. We'll give you an accurate time estimate when you book."
  },
  {
    question: "Can you come to my workshop or depot?",
    answer: "Absolutely. We offer mobile calibration services across Adelaide and throughout South Australia. We regularly visit windscreen fitters, panel shops, dealerships, and fleet depots to perform on-site calibrations. This saves you time and means vehicles don't need to be transported to a separate facility. Just let us know your location when you book, and we'll come to you with all the equipment needed."
  },
  {
    question: "What documentation do I get after calibration?",
    answer: "Every calibration we perform comes with comprehensive documentation. This includes a pre-calibration diagnostic scan report (showing any existing faults), the calibration completion report confirming the procedure was carried out to OEM specifications, and a post-calibration scan report verifying the system is functioning correctly. This documentation package is essential for insurers, fleet managers, and your own records, providing proof that the work was completed properly."
  }
];

export const WindscreenCalibration = () => {
  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>ADAS Calibration After Windscreen Replacement Adelaide | Advanced ADAS</title>
        <meta name="description" content="Windscreen replaced? Your ADAS camera must be recalibrated. Mobile, OEM-compliant windscreen camera calibration across Adelaide with full documentation." />
        <link rel="canonical" href="https://advancedadas.com.au/windscreen-calibration-adelaide" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="ADAS Calibration After Windscreen Replacement Adelaide | Advanced ADAS" />
        <meta property="og:description" content="Windscreen replaced? Your ADAS camera must be recalibrated. Mobile, OEM-compliant windscreen camera calibration across Adelaide with full documentation." />
        <meta property="og:url" content="https://advancedadas.com.au/windscreen-calibration-adelaide" />
        <meta property="og:locale" content="en_AU" />
      </Helmet>

      <div className="pt-20 md:pt-24">
        {/* Hero Section */}
        <section className="bg-slate-900 py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6">
                <a href="/" className="hover:text-white transition-colors">Home</a>
                <ChevronRight className="w-4 h-4" />
                <span className="text-blue-400">Windscreen Calibration</span>
              </nav>
              
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
                ADAS Camera Calibration After Windscreen Replacement in Adelaide
              </h1>
              
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                When a windscreen is replaced, the forward-facing camera mounted on or behind the glass must be recalibrated to the vehicle manufacturer's exact specifications. Without proper recalibration, critical safety systems such as lane keeping assist, automatic emergency braking, and adaptive cruise control can fail to operate correctly or misread the road ahead. These systems rely on precise camera alignment—even a millimetre of deviation can cause significant errors in how the vehicle perceives its surroundings.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:0402454588">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto">
                    <Phone className="w-4 h-4 mr-2" />
                    Call 0402 454 588
                  </Button>
                </a>
                <Button 
                  variant="outline" 
                  className="border-slate-600 text-white hover:bg-slate-800 w-full sm:w-auto"
                  onClick={scrollToContact}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Enquiry
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <article className="py-16 md:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Why Recalibration Is Required */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Why Recalibration Is Required
              </h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-600 leading-relaxed mb-4">
                  Vehicle manufacturers design ADAS cameras with extremely tight tolerances. When a windscreen is removed and replaced, the camera's mounting position inevitably shifts, even if only slightly. This is why every major vehicle manufacturer includes camera recalibration as a mandatory step in their official windscreen replacement procedures.
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Beyond manufacturer requirements, insurers increasingly require documented proof of recalibration following windscreen work. This protects both the vehicle owner and the repairer from liability issues. If an ADAS-related incident occurs after a windscreen replacement and there's no record of recalibration, questions about responsibility can quickly arise.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Most importantly, recalibration is a safety issue. Lane departure warnings, forward collision alerts, and automatic braking systems are designed to protect lives. When these systems aren't calibrated correctly, they may not respond when needed, or they may respond inappropriately—both scenarios that put drivers, passengers, and other road users at risk.
                </p>
              </div>
            </motion.section>

            {/* Mobile Windscreen Calibration */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Mobile Windscreen Calibration — We Come to You
              </h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-600 leading-relaxed mb-4">
                  We understand that moving vehicles between locations adds time and cost to every job. That's why we offer fully mobile ADAS calibration services across Adelaide and throughout South Australia. Our technicians travel to windscreen fitters, panel shops, dealerships, and fleet depots with all the equipment needed to perform professional calibrations on-site.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Whether you're a windscreen replacement company needing same-day calibration support, a collision repairer completing a repair job, or a fleet manager with multiple vehicles requiring attention, we can come to your location and complete the work efficiently. We also welcome customers at our workshop facility for those who prefer to bring vehicles to us.
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900">On-Site Service</p>
                    <p className="text-sm text-slate-600">We come to your workshop, depot, or dealership</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900">Same-Day Available</p>
                    <p className="text-sm text-slate-600">Fast turnaround to keep your workflow moving</p>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Static and Dynamic Calibration */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Static and Dynamic Calibration
              </h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-600 leading-relaxed mb-4">
                  ADAS camera calibration is performed using one of two methods, depending on the vehicle manufacturer's requirements: static calibration or dynamic calibration.
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  <strong className="text-slate-900">Static calibration</strong> is performed in a controlled environment using specialised targets and equipment. The vehicle remains stationary while the calibration system precisely aligns the camera using manufacturer-specified target boards positioned at exact distances and heights. This method is the most common for windscreen camera calibration and can be performed at your location or our workshop.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  <strong className="text-slate-900">Dynamic calibration</strong> requires the vehicle to be driven on public roads under specific conditions—certain speeds, road markings, and driving distances—while the system self-calibrates using real-world references. Some vehicles require dynamic calibration alone, while others need both static and dynamic procedures. We determine the correct method for each vehicle based on manufacturer specifications.
                </p>
              </div>
            </motion.section>

            {/* Independent & Insurer-Ready */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Independent & Insurer-Ready
              </h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-600 leading-relaxed mb-4">
                  Advanced ADAS Calibrations is an independent specialist—we're not tied to any glass company, dealership group, or insurance provider. This independence means we focus solely on delivering quality calibration services using OEM-compliant procedures and equipment.
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Every calibration we perform includes comprehensive documentation: a pre-calibration diagnostic scan identifying any existing faults, the calibration procedure completed to manufacturer specifications, and a post-calibration verification scan confirming successful completion. This documentation package satisfies insurer requirements and provides clear proof that the work was done correctly.
                </p>
              </div>
              
              <div className="grid sm:grid-cols-3 gap-4 mt-6">
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900">OEM Procedures</p>
                    <p className="text-sm text-slate-600">Manufacturer specifications followed</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900">Full Documentation</p>
                    <p className="text-sm text-slate-600">Pre and post-calibration reports</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900">Insurer Accepted</p>
                    <p className="text-sm text-slate-600">Reports meet insurer requirements</p>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Who We Work With */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Who We Work With
              </h2>
              <div className="prose prose-slate max-w-none mb-6">
                <p className="text-slate-600 leading-relaxed">
                  We provide windscreen camera calibration services to businesses and organisations across the automotive industry in Adelaide and South Australia:
                </p>
              </div>
              
              <ul className="grid sm:grid-cols-2 gap-3">
                {[
                  "Windscreen and auto glass fitters",
                  "Collision repair centres",
                  "Motor vehicle insurers",
                  "Fleet operators and managers",
                  "New and used car dealerships",
                  "Mechanical workshops"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.section>

            {/* FAQ Section */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
                Frequently Asked Questions
              </h2>
              
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, index) => (
                  <AccordionItem 
                    key={index} 
                    value={`item-${index}`}
                    className="bg-slate-50 rounded-lg px-4 border-none"
                  >
                    <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.section>

            {/* Call to Action */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-slate-900 rounded-2xl p-8 md:p-10 text-center"
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
                Book Your Windscreen Camera Calibration
              </h2>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Need ADAS calibration after a windscreen replacement? Contact us today to arrange mobile service at your location or book an appointment at our workshop.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <a href="tel:0402454588">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto">
                    <Phone className="w-4 h-4 mr-2" />
                    0402 454 588
                  </Button>
                </a>
                <a href="mailto:sales@advancedadas.com.au">
                  <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800 w-full sm:w-auto">
                    <Mail className="w-4 h-4 mr-2" />
                    sales@advancedadas.com.au
                  </Button>
                </a>
              </div>
              
              <Button 
                variant="link" 
                className="text-blue-400 hover:text-blue-300"
                onClick={scrollToContact}
              >
                Or fill out our online enquiry form →
              </Button>
            </motion.section>

          </div>
        </article>
      </div>
    </>
  );
};
